import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatErr, fmtDate, fmtMoney, setImpersonateOrg, getImpersonateOrg } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Drawer } from "../components/UI";
import { Page } from "../components/AppShell";
import PasswordInput from "../components/PasswordInput";
import {
  Buildings, CurrencyDollar, ChartLine, MagnifyingGlass, UserSwitch,
  ShieldCheck, Sparkle, CircleNotch, ArrowRight, Warning, Clock,
  Plus, Key,
} from "@phosphor-icons/react";
import UnificationTab from "../components/DevUnificationTab";

/**
 * Iter 29 — Developer Console.
 *
 * Three tabs: Orgs Directory, Platform Billing, Growth Funnel. Gated by
 * `user.is_developer_user`. Impersonation lives in the Orgs tab —
 * clicking IMPERSONATE on a row sets the X-Impersonate-Org-Id header
 * globally and pops a sticky red banner with a stop button.
 */
export default function DevConsole() {
  const { user } = useAuth();
  const [tab, setTab] = useState("orgs");
  const navigate = useNavigate();

  if (!user) return null;
  if (!user.is_developer_user) {
    return (
      <div className="p-12 max-w-xl mx-auto text-center" data-testid="dev-denied">
        <ShieldCheck size={36} weight="duotone" className="mx-auto mb-3 text-muted-foreground" />
        <h2 className="text-2xl font-semibold">Developer access required</h2>
        <p className="text-sm text-muted-foreground mt-2">
          Your account is not flagged as a developer user. Ask an existing developer to promote you via the console.
        </p>
        <button className="btn btn-secondary mt-6" onClick={() => navigate("/")} data-testid="dev-denied-home">
          BACK TO DASHBOARD
        </button>
      </div>
    );
  }

  return (
    <Page
      title="Developer Console"
      subtitle="PLATFORM ADMIN · INTERNAL"
      breadcrumbs={[{ label: "Dashboard", to: "/" }, "Developer Console"]}
      testid="dev-console-page"
      actions={<span className="text-xs mono text-muted-foreground">Logged in as <strong>{user.email}</strong></span>}
    >
      <div className="max-w-7xl mx-auto">

      <div className="flex gap-2 border-b border-border mb-6" data-testid="dev-tabs">
        <TabBtn id="orgs" cur={tab} onClick={setTab} icon={Buildings} label="Orgs Directory" testid="tab-orgs" />
        <TabBtn id="billing" cur={tab} onClick={setTab} icon={CurrencyDollar} label="Platform Billing" testid="tab-billing" />
        <TabBtn id="growth" cur={tab} onClick={setTab} icon={ChartLine} label="Growth Funnel" testid="tab-growth" />
        <TabBtn id="audit" cur={tab} onClick={setTab} icon={Clock} label="Recent Impersonations" testid="tab-audit" />
        <TabBtn id="unify" cur={tab} onClick={setTab} icon={Key} label="Org Unification" testid="tab-unify" />
      </div>

      {tab === "orgs"    && <OrgsTab />}
      {tab === "billing" && <BillingTab />}
      {tab === "growth"  && <GrowthTab />}
      {tab === "audit"   && <AuditTab />}
      {tab === "unify"   && <UnificationTab />}
      </div>
    </Page>
  );
}

// ---------------------------------------------------------------
// Tabs
// ---------------------------------------------------------------

const TabBtn = ({ id, cur, onClick, icon: Icon, label, testid }) => (
  <button
    className={`flex items-center gap-2 px-4 py-2 -mb-px border-b-2 text-sm transition-colors ${
      cur === id ? "border-blue-600 text-foreground font-medium" : "border-transparent text-muted-foreground hover:text-foreground"
    }`}
    onClick={() => onClick(id)}
    data-testid={testid}
  >
    <Icon size={14} weight="duotone" /> {label}
  </button>
);

// ---------------------------------------------------------------
// Orgs Directory + Impersonation
// ---------------------------------------------------------------

function OrgsTab() {
  const [orgs, setOrgs] = useState([]);
  const [q, setQ] = useState("");
  const [orgType, setOrgType] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [openNew, setOpenNew] = useState(false);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (orgType) params.set("org_type", orgType);
    api.get(`/dev/orgs?${params.toString()}`)
      .then((r) => setOrgs(r.data))
      .catch((e) => setErr(formatErr(e)))
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="orgs-tab">
      <div className="flex gap-2 items-center mb-4">
        <div className="surface flex items-center gap-2 px-3 py-1.5 flex-1">
          <MagnifyingGlass size={14} weight="duotone" className="text-muted-foreground" />
          <input
            className="bg-transparent outline-none text-sm flex-1"
            placeholder="Search by org name or user email…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            data-testid="orgs-search"
          />
        </div>
        <select
          className="surface px-3 py-1.5 text-sm"
          value={orgType}
          onChange={(e) => { setOrgType(e.target.value); }}
          data-testid="orgs-type-filter"
        >
          <option value="">All types</option>
          <option value="core">core</option>
          <option value="customer">customer</option>
          <option value="contractor">contractor</option>
          <option value="platform">platform</option>
        </select>
        <button className="btn btn-secondary" onClick={load} data-testid="orgs-refresh">REFRESH</button>
        <button className="btn btn-primary" onClick={() => setOpenNew(true)} data-testid="orgs-new-btn">
          <Plus size={12} /> NEW ORG
        </button>
      </div>

      {err && <div className="text-xs text-destructive mb-3" data-testid="orgs-error">{err}</div>}

      {loading ? (
        <div className="p-12 text-center text-muted-foreground" data-testid="orgs-loading">
          <CircleNotch className="animate-spin inline mr-2" /> Loading orgs…
        </div>
      ) : orgs.length === 0 ? (
        <div className="surface p-12 text-center text-muted-foreground" data-testid="orgs-empty">No orgs match these filters.</div>
      ) : (
        <div className="surface">
          <table className="dense">
            <thead><tr>
              <th>NAME</th><th>TYPE</th><th>PLAN</th><th>BILLING</th><th>USERS</th><th>JOBS</th><th>LAST ACTIVE</th><th>CREATED</th><th></th>
            </tr></thead>
            <tbody>
              {orgs.map((o) => <OrgRow key={o.id} org={o} />)}
            </tbody>
          </table>
        </div>
      )}

      <NewOrgDrawer
        open={openNew}
        onClose={() => setOpenNew(false)}
        onCreated={() => { setOpenNew(false); load(); }}
      />
    </div>
  );
}


function NewOrgDrawer({ open, onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", org_type: "core", trial_days: 14,
    admin_name: "", admin_email: "", admin_password: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) {
      setForm({ name: "", org_type: "core", trial_days: 14, admin_name: "", admin_email: "", admin_password: "" });
      setErr("");
      setResult(null);
    }
  }, [open]);

  const submit = async () => {
    if (!form.name.trim() || !form.admin_email.trim() || (form.admin_password || "").length < 8) {
      setErr("Org name, admin email, and an 8+ character password are required.");
      return;
    }
    setSubmitting(true); setErr("");
    try {
      const r = await api.post("/dev/orgs", form);
      setResult(r.data);
      setTimeout(() => { if (onCreated) onCreated(r.data); }, 1500);
    } catch (e) { setErr(formatErr(e)); }
    finally { setSubmitting(false); }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="New Org (Developer)"
      testid="dev-new-org-drawer"
      footer={<>
        <button className="btn btn-secondary" onClick={onClose} disabled={submitting} data-testid="dev-new-org-cancel">CANCEL</button>
        <button className="btn btn-primary" onClick={submit} disabled={submitting || !!result} data-testid="dev-new-org-submit">
          {submitting ? "CREATING…" : (result ? "CREATED" : "CREATE")}
        </button>
      </>}
    >
      {result ? (
        <div className="space-y-3" data-testid="dev-new-org-success">
          <div className="surface p-3 text-green-600 text-sm font-medium">✓ Org created</div>
          <div className="surface p-3 text-xs mono">
            <div>org_id: {result.org.id}</div>
            <div>name: {result.org.name}</div>
            <div>type: {result.org.org_type}</div>
            <div>plan: {result.org.plan} ({result.org.trial_days_override} day trial)</div>
            <div className="mt-2 pt-2 border-t border-border">
              admin: {result.admin_user.name} &lt;{result.admin_user.email}&gt;
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-3" data-testid="dev-new-org-form">
          <div>
            <div className="label-xs mb-1">ORG NAME</div>
            <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="dev-new-org-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="label-xs mb-1">ORG TYPE</div>
              <select className="input" value={form.org_type} onChange={(e) => setForm({ ...form, org_type: e.target.value })} data-testid="dev-new-org-type">
                <option value="core">core</option>
                <option value="customer">customer</option>
                <option value="contractor">contractor</option>
                <option value="platform">platform</option>
              </select>
            </div>
            <div>
              <div className="label-xs mb-1">TRIAL DAYS</div>
              <input type="number" min={1} max={365} className="input" value={form.trial_days} onChange={(e) => setForm({ ...form, trial_days: parseInt(e.target.value || "14", 10) })} data-testid="dev-new-org-trial-days" />
            </div>
          </div>
          <div className="pt-3 border-t border-border">
            <div className="label-xs mb-2 text-muted-foreground">INITIAL ADMIN</div>
            <div className="space-y-3">
              <input className="input" placeholder="Admin name" value={form.admin_name} onChange={(e) => setForm({ ...form, admin_name: e.target.value })} data-testid="dev-new-org-admin-name" />
              <input className="input" type="email" placeholder="admin@example.com" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} data-testid="dev-new-org-admin-email" />
              <PasswordInput className="input" placeholder="Password (8+ chars)" value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} data-testid="dev-new-org-admin-password" />
            </div>
          </div>
          {err && <div className="text-xs text-destructive" data-testid="dev-new-org-error">{err}</div>}
        </div>
      )}
    </Drawer>
  );
}

function OrgRow({ org }) {
  const [busy, setBusy] = useState(false);
  const startImpersonate = async () => {
    if (!window.confirm(`Impersonate ${org.name}? This is logged to the audit trail.`)) return;
    setBusy(true);
    try {
      await api.post(`/dev/impersonate/${org.id}`, { reason: "dev console" });
      setImpersonateOrg(org.id);
      window.location.href = "/";
    } catch (e) {
      alert(formatErr(e));
      setBusy(false);
    }
  };
  return (
    <tr data-testid={`org-row-${org.id}`}>
      <td className="text-sm">
        <div className="font-medium">{org.name}</div>
        <div className="text-xs text-muted-foreground mono">{org.id.slice(0, 8)}</div>
      </td>
      <td className="text-xs mono uppercase"><Badge type={org.org_type} /></td>
      <td className="text-xs mono uppercase">{org.plan || "—"}</td>
      <td className="text-xs">
        {org.billing_active
          ? <span className="text-green-600 font-medium">● ACTIVE</span>
          : <span className="text-muted-foreground">○ inactive</span>}
      </td>
      <td className="text-xs mono">{org.users_count}</td>
      <td className="text-xs mono">{org.jobs_count}</td>
      <td className="text-xs text-muted-foreground mono">{org.last_active_at ? fmtDate(org.last_active_at) : "—"}</td>
      <td className="text-xs text-muted-foreground mono">{fmtDate(org.created_at)}</td>
      <td>
        <button
          className="btn btn-ghost btn-sm"
          onClick={startImpersonate}
          disabled={busy}
          data-testid={`impersonate-${org.id}`}
          title="Act as an admin of this org (audit-logged)"
        >
          <UserSwitch size={12} /> {busy ? "STARTING…" : "IMPERSONATE"}
        </button>
      </td>
    </tr>
  );
}

const Badge = ({ type }) => {
  const map = {
    core: { bg: "#10b981", label: "CORE" },
    customer: { bg: "#3b82f6", label: "CUSTOMER" },
    contractor: { bg: "#f59e0b", label: "CONTRACTOR" },
    developer: { bg: "#8b5cf6", label: "DEV" },
  };
  const t = map[type] || { bg: "#94a3b8", label: (type || "—").toUpperCase() };
  return (
    <span className="inline-block px-2 py-0.5 text-[10px] font-medium rounded" style={{ background: t.bg, color: "white" }}>
      {t.label}
    </span>
  );
};

// Sticky red banner shown ONLY while impersonating.
export function ImpersonationBanner() {
  const [active, setActive] = useState(null);
  const { refresh } = useAuth();

  useEffect(() => {
    api.get("/dev/impersonation/active").then((r) => setActive(r.data)).catch(() => {});
  }, []);

  const stop = async () => {
    try { await api.post("/dev/stop-impersonation"); } catch { /* ignore */ }
    setImpersonateOrg(null);
    setActive(null);
    await refresh?.();
    window.location.reload();
  };

  if (!active) return null;
  const matchesHeader = getImpersonateOrg() === active.target_org_id;
  return (
    <div
      className="px-4 py-3 mb-4 flex items-center gap-3 border-l-4 rounded"
      style={{ borderLeftColor: "#dc2626", background: "rgba(220,38,38,0.08)" }}
      data-testid="impersonation-banner"
    >
      <Warning size={18} weight="fill" className="text-red-600" />
      <div className="flex-1 text-sm">
        <span className="font-medium text-red-700">Impersonating {active.target_org_name}</span>
        <span className="text-muted-foreground"> · started {fmtDate(active.started_at)} · audit_id {active.audit_id.slice(0, 8)}</span>
        {!matchesHeader && <span className="text-xs ml-2 text-amber-700">⚠ header not active — click STOP & re-impersonate</span>}
      </div>
      <button className="btn btn-primary btn-sm" style={{ background: "#dc2626" }} onClick={stop} data-testid="stop-impersonation">
        STOP IMPERSONATION
      </button>
    </div>
  );
}

// ---------------------------------------------------------------
// Platform Billing Dashboard
// ---------------------------------------------------------------

function BillingTab() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get("/dev/billing/dashboard")
      .then((r) => setData(r.data))
      .catch((e) => setErr(formatErr(e)));
  }, []);

  if (err) return <div className="text-xs text-destructive" data-testid="billing-error">{err}</div>;
  if (!data) return <div className="p-12 text-center text-muted-foreground" data-testid="billing-loading"><CircleNotch className="animate-spin inline mr-2" /> Loading…</div>;

  return (
    <div data-testid="billing-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Stat label="ESTIMATED MRR"        value={fmtMoney(data.estimated_mrr_usd)} testid="stat-mrr" />
        <Stat label="ACTIVE SUBSCRIPTIONS" value={data.active_subscriptions} testid="stat-active-subs" />
        <Stat label="ACTIVE TRIALS"        value={data.active_trials} testid="stat-trials" />
        <Stat label="CHURN (30D)"          value={data.churn_30d} testid="stat-churn" />
        <Stat label="CONVERSIONS (30D)"    value={data.conversions_30d} testid="stat-conversions" />
        <Stat label="PAID JOBS (30D)"      value={data.paid_jobs_30d} testid="stat-paid-jobs" />
        <Stat label="BASE PRICE / MO"      value={`$${data.pricing.base_per_month_usd.toFixed(2)}`} testid="stat-base" />
        <Stat label="PER JOB"              value={`$${data.pricing.per_job_usd.toFixed(2)}`} testid="stat-per-job" />
      </div>

      <h3 className="text-sm uppercase mono text-muted-foreground tracking-widest mb-2">Top orgs by jobs (30 days)</h3>
      <div className="surface">
        <table className="dense">
          <thead><tr><th>ORG</th><th>TYPE</th><th>BILLING</th><th>JOBS (30D)</th></tr></thead>
          <tbody>
            {data.top_orgs_by_jobs_30d.map((o) => (
              <tr key={o.id} data-testid={`top-org-${o.id}`}>
                <td className="text-sm">{o.name}</td>
                <td><Badge type={o.org_type} /></td>
                <td className="text-xs">{o.billing_active ? <span className="text-green-600">● ACTIVE</span> : <span className="text-muted-foreground">○</span>}</td>
                <td className="text-xs mono">{o.jobs_30d}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const Stat = ({ label, value, testid }) => (
  <div className="surface p-4" data-testid={testid}>
    <div className="text-[10px] mono uppercase text-muted-foreground tracking-widest">{label}</div>
    <div className="text-2xl font-semibold mt-1">{value}</div>
  </div>
);

// ---------------------------------------------------------------
// Growth Funnel
// ---------------------------------------------------------------

function GrowthTab() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [days, setDays] = useState(90);

  const load = (d) => {
    setData(null); setErr("");
    api.get(`/dev/growth/funnel?days=${d}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(formatErr(e)));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(days); }, [days]);

  if (err) return <div className="text-xs text-destructive" data-testid="growth-error">{err}</div>;
  if (!data) return <div className="p-12 text-center text-muted-foreground" data-testid="growth-loading"><CircleNotch className="animate-spin inline mr-2" /> Loading…</div>;

  const maxN = Math.max(...data.steps.map((s) => s.count), 1);

  return (
    <div data-testid="growth-tab">
      <div className="flex items-center gap-3 mb-4">
        <span className="text-xs uppercase mono text-muted-foreground">WINDOW</span>
        {[30, 90, 180, 365].map((d) => (
          <button
            key={d}
            className={`btn btn-sm ${d === days ? "btn-primary" : "btn-ghost"}`}
            onClick={() => setDays(d)}
            data-testid={`window-${d}`}
          >{d}D</button>
        ))}
      </div>

      <div className="surface p-6" data-testid="growth-funnel">
        {data.steps.map((s, i) => (
          <div key={s.key} className="mb-4 last:mb-0">
            <div className="flex justify-between items-baseline mb-1">
              <div>
                <span className="text-[10px] mono uppercase text-muted-foreground tracking-widest">STEP {i + 1}</span>
                <span className="ml-2 font-medium">{s.label}</span>
              </div>
              <div className="text-sm mono">
                <span className="font-semibold" data-testid={`step-${s.key}-count`}>{s.count}</span>
                <span className="text-muted-foreground ml-2" data-testid={`step-${s.key}-pct`}>{s.pct}%</span>
              </div>
            </div>
            <div className="h-2 bg-muted/30 rounded">
              <div
                className="h-2 rounded transition-all"
                style={{
                  width: `${(s.count / maxN) * 100}%`,
                  background: i === 0 ? "#3b82f6" : i === 1 ? "#22c55e" : i === 2 ? "#f59e0b" : "#8b5cf6",
                }}
              />
            </div>
            {data.drop_offs[i] && (
              <div className="text-[10px] mono text-muted-foreground mt-1 ml-4 flex items-center gap-1">
                <ArrowRight size={10} /> {data.drop_offs[i].lost} dropped off
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="text-xs text-muted-foreground mt-3 flex items-center gap-2">
        <Sparkle size={12} weight="duotone" />
        Track contractor-to-Core conversion. Step 3 (clicked) is logged the moment a user hits the UPGRADE button; step 4 is fired on successful Stripe checkout.
      </div>
    </div>
  );
}


// ---------------------------------------------------------------
// Recent Impersonations — audit feed
// ---------------------------------------------------------------

function AuditTab() {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get("/dev/impersonation/audit?limit=50")
      .then((r) => setRows(r.data))
      .catch((e) => setErr(formatErr(e)));
  }, []);

  if (err) return <div className="text-xs text-destructive" data-testid="audit-error">{err}</div>;
  if (!rows) return <div className="p-12 text-center text-muted-foreground" data-testid="audit-loading"><CircleNotch className="animate-spin inline mr-2" /> Loading…</div>;

  return (
    <div data-testid="audit-tab">
      <div className="flex items-center gap-2 mb-4">
        <Clock size={14} weight="duotone" className="text-muted-foreground" />
        <span className="text-xs uppercase mono text-muted-foreground tracking-widest">LAST 50 IMPERSONATION SESSIONS</span>
      </div>

      {rows.length === 0 ? (
        <div className="surface p-12 text-center text-muted-foreground" data-testid="audit-empty">
          No impersonations logged yet.
        </div>
      ) : (
        <div className="surface">
          <table className="dense">
            <thead><tr>
              <th>WHEN</th><th>DEVELOPER</th><th>TARGET ORG</th><th>DURATION</th><th>REASON</th><th>IP</th><th>STATUS</th>
            </tr></thead>
            <tbody>
              {rows.map((a) => <AuditRow key={a.id} a={a} />)}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AuditRow({ a }) {
  const active = !a.ended_at;
  const duration = active
    ? "active"
    : (() => {
        const ms = new Date(a.ended_at) - new Date(a.started_at);
        const s = Math.max(0, Math.round(ms / 1000));
        if (s < 60) return `${s}s`;
        if (s < 3600) return `${Math.round(s / 60)}m`;
        return `${(s / 3600).toFixed(1)}h`;
      })();
  return (
    <tr data-testid={`audit-row-${a.id}`}>
      <td className="text-xs text-muted-foreground mono">{fmtDate(a.started_at)}</td>
      <td className="text-xs">{a.developer_email || a.developer_user_id.slice(0, 8)}</td>
      <td className="text-sm font-medium">{a.target_org_name || a.target_org_id.slice(0, 8)}</td>
      <td className="text-xs mono">{duration}</td>
      <td className="text-xs text-muted-foreground" title={a.reason || ""}>
        {a.reason ? (a.reason.length > 40 ? a.reason.slice(0, 40) + "…" : a.reason) : "—"}
      </td>
      <td className="text-xs mono text-muted-foreground">{a.ip || "—"}</td>
      <td className="text-xs">
        {active
          ? <span className="text-amber-600 font-medium">● LIVE</span>
          : <span className="text-muted-foreground">○ ended</span>}
      </td>
    </tr>
  );
}
